import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';

import { authHandlers } from '../../api_handlers/auth';
import InfluencerLogo from '../../assets/influencer-logo.png';
import { InstagramMark, YoutubeMark } from './icons';
import {
  instagramLoginAvailable,
  startInstagramLogin,
  useInstagramLogin,
  useYoutubeLogin,
} from './socialAuth';

const CSS = `
@keyframes inf-float {
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(0, -26px, 0) scale(1.06); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
}
@keyframes inf-rise {
  from { opacity: 0; transform: translate3d(0, 14px, 0); }
  to   { opacity: 1; transform: none; }
}
@keyframes inf-spin { to { transform: rotate(360deg); } }

/* The app does not set a global border-box, and without it the section
   padding pushes these panels past the viewport width. */
.inf-login, .inf-login *, .inf-login *::before, .inf-login *::after { box-sizing: border-box; }
.inf-login { min-height: 100vh; display: flex; font-family: 'Lato', 'Helvetica Neue', Arial, sans-serif; color: #1b1d21; overflow-x: hidden; }
.inf-login__brand {
  flex: 1 1 52%; position: relative; overflow: hidden; display: flex;
  flex-direction: column; justify-content: center; padding: 64px 72px;
  background: linear-gradient(140deg, #12141a 0%, #1b1f2e 45%, #241a33 100%);
  color: #fff;
}
.inf-login__blob { position: absolute; border-radius: 50%; filter: blur(70px); opacity: .55; pointer-events: none; }
.inf-login__blob--a { width: 420px; height: 420px; top: -110px; left: -90px; background: radial-gradient(circle, #f9377d 0%, transparent 70%); animation: inf-float 13s ease-in-out infinite; }
.inf-login__blob--b { width: 380px; height: 380px; bottom: -120px; right: -60px; background: radial-gradient(circle, #1e9cb2 0%, transparent 70%); animation: inf-float 17s ease-in-out infinite reverse; }
.inf-login__blob--c { width: 300px; height: 300px; top: 42%; right: 18%; background: radial-gradient(circle, #f6a935 0%, transparent 70%); animation: inf-float 21s ease-in-out infinite; }
.inf-login__brandinner { position: relative; z-index: 1; max-width: 520px; animation: inf-rise .7s ease both; }
.inf-login__eyebrow { font-size: 12px; letter-spacing: .16em; text-transform: uppercase; color: #8de3f0; margin: 0 0 18px; font-weight: 700; }
.inf-login__headline { font-size: 44px; line-height: 1.12; font-weight: 800; margin: 0 0 18px; letter-spacing: -.02em; }
.inf-login__headline em { font-style: normal; background: linear-gradient(90deg, #f9377d, #f6a935); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.inf-login__sub { font-size: 16px; line-height: 1.65; color: rgba(255,255,255,.72); margin: 0 0 38px; }
.inf-login__points { list-style: none; margin: 0; padding: 0; display: grid; gap: 16px; }
.inf-login__point { display: flex; gap: 14px; align-items: flex-start; font-size: 15px; color: rgba(255,255,255,.9); }
.inf-login__tick { flex: none; width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center; background: rgba(255,255,255,.12); font-size: 12px; }

.inf-login__panel { flex: 1 1 48%; display: flex; align-items: center; justify-content: center; padding: 48px 24px; background: #fff; }
.inf-login__card { width: 100%; max-width: 420px; animation: inf-rise .7s .08s ease both; }
.inf-login__logo { height: 32px; margin-bottom: 34px; }
.inf-login__title { font-size: 28px; font-weight: 800; margin: 0 0 8px; letter-spacing: -.01em; }
.inf-login__lede { font-size: 15px; color: #6b7280; margin: 0 0 32px; line-height: 1.6; }

.inf-btn {
  width: 100%; border: 0; border-radius: 12px; padding: 15px 18px; cursor: pointer;
  font-size: 15.5px; font-weight: 700; color: #fff; display: flex; align-items: center;
  justify-content: center; gap: 12px; font-family: inherit;
  transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
}
.inf-btn + .inf-btn { margin-top: 14px; }
.inf-btn:hover:not(:disabled) { transform: translateY(-2px); }
.inf-btn:active:not(:disabled) { transform: translateY(0); }
.inf-btn:disabled { opacity: .6; cursor: not-allowed; }
.inf-btn:focus-visible { outline: 3px solid #1e9cb2; outline-offset: 3px; }
.inf-btn--ig { background: linear-gradient(95deg, #4f5bd5 0%, #962fbf 25%, #d62976 50%, #fa7e1e 80%, #feda75 100%); box-shadow: 0 8px 22px rgba(214,41,118,.28); }
.inf-btn--ig:hover:not(:disabled) { box-shadow: 0 14px 30px rgba(214,41,118,.36); filter: saturate(1.08); }
.inf-btn--yt { background: #ff0033; box-shadow: 0 8px 22px rgba(255,0,51,.24); }
.inf-btn--yt:hover:not(:disabled) { box-shadow: 0 14px 30px rgba(255,0,51,.32); filter: saturate(1.05); }

.inf-login__hint { font-size: 12.5px; color: #8a9099; line-height: 1.6; margin: 16px 0 0; text-align: center; }
.inf-login__error { margin: 20px 0 0; padding: 12px 14px; border-radius: 10px; background: #fdecec; color: #b3261e; font-size: 13.5px; line-height: 1.5; }
.inf-login__spinner { width: 17px; height: 17px; border-radius: 50%; border: 2px solid rgba(255,255,255,.45); border-top-color: #fff; animation: inf-spin .7s linear infinite; }
.inf-login__divider { display: flex; align-items: center; gap: 12px; margin: 30px 0 22px; color: #b4b9c0; font-size: 12px; }
.inf-login__divider::before, .inf-login__divider::after { content: ''; flex: 1; height: 1px; background: #e8eaed; }
.inf-login__alt { text-align: center; font-size: 14px; color: #6b7280; }
.inf-login__link { color: #1e9cb2; font-weight: 700; cursor: pointer; text-decoration: none; background: none; border: 0; font-size: inherit; font-family: inherit; padding: 0; }
.inf-login__link:hover { text-decoration: underline; }
.inf-login__legal { margin-top: 34px; font-size: 11.5px; line-height: 1.7; color: #9aa0a8; text-align: center; }
.inf-login__legal a { color: #9aa0a8; text-decoration: none; }
.inf-login__legal a:hover { color: #6b7280; text-decoration: underline; }

@media (max-width: 900px) {
  .inf-login { flex-direction: column; }
  /* Sign-in first on a phone: the buttons are the reason for the page, so
     they stay above the fold and the pitch moves underneath. */
  .inf-login__panel { order: -1; flex: none; padding: 34px 20px 40px; }
  .inf-login__brand { flex: none; padding: 40px 20px 46px; }
  .inf-login__headline { font-size: 27px; }
  .inf-login__sub { font-size: 15px; margin-bottom: 24px; }
  .inf-login__brandinner { max-width: none; }
  .inf-login__card { max-width: 460px; }
  .inf-login__logo { margin-bottom: 24px; }
  .inf-login__title { font-size: 25px; }
  .inf-login__lede { margin-bottom: 26px; }
  .inf-login__legal { margin-top: 26px; }
}
@media (prefers-reduced-motion: reduce) {
  .inf-login__blob, .inf-login__brandinner, .inf-login__card { animation: none; }
  .inf-btn { transition: none; }
}
`;

const POINTS = [
  'See every campaign you are shortlisted on',
  'Track deliverables and post performance',
  'Raise invoices and follow payments to payout',
];

/**
 * public/index.html pins the whole app to `width=1280`, so on a phone every
 * page renders as a scaled-down desktop layout and CSS media queries never
 * fire. A login page is the one people most often open on a phone, so this
 * page opts itself into a real responsive viewport and puts the app-wide
 * value back when it unmounts.
 */
function useResponsiveViewport() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1');
    return () => meta.setAttribute('content', previous);
  }, []);
}

function InfluencerLogin({ history }) {
  const [pending, setPending] = useState(null); // 'instagram' | 'youtube'
  const [error, setError] = useState('');

  useResponsiveViewport();

  useEffect(() => {
    if (!localStorage.getItem('access')) return;
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return;
    }
    if (user && user.user_type === 'influencer') {
      history.push('/influencer/campaigns');
    } else if (user && user.user_type === 'brand') {
      history.push('/planner/discover');
    }
  }, [history]);

  const finish = useCallback(
    async (provider, request) => {
      setError('');
      setPending(provider);
      try {
        const user = await request();
        if (user) {
          // The same Facebook button serves brands, and the API keeps their
          // account type intact -- sending them into the creator app would
          // strand them on an empty campaign list.
          history.push(
            user.user_type === 'brand' ? '/planner/discover' : '/influencer/campaigns',
          );
        } else {
          setError(
            'We could not sign you in with that account. Please try again, or contact us if it keeps happening.',
          );
          setPending(null);
        }
      } catch (e) {
        setError('Something went wrong while signing you in. Please try again.');
        setPending(null);
      }
    },
    [history],
  );

  const handleCancelled = useCallback((e) => {
    setPending(null);
    if (e && e.message && e.message !== 'cancelled') {
      setError(e.message);
    }
  }, []);

  // The Facebook route is kept wired up -- it is still how brands connect,
  // and how every creator connected before Instagram Login existed -- but it
  // is no longer offered here. Creators go straight to Instagram.
  const [startFacebookInstagram, instagramReady] = useInstagramLogin({
    onSuccess: (authResponse) =>
      finish('instagram', () => authHandlers.facebookLogin(authResponse)),
    onError: handleCancelled,
  });

  const useDirectInstagram = instagramLoginAvailable;
  const startInstagram = useDirectInstagram
    ? () => {
        setPending('instagram');
        startInstagramLogin();
      }
    : startFacebookInstagram;

  const startYoutube = useYoutubeLogin({
    onSuccess: (response) => finish('youtube', () => authHandlers.youtubeLogin(response)),
    onError: handleCancelled,
  });

  const busy = pending !== null;

  return (
    <>
      <style>{CSS}</style>
      <div className="inf-login">
        <section className="inf-login__brand">
          <span className="inf-login__blob inf-login__blob--a" />
          <span className="inf-login__blob inf-login__blob--b" />
          <span className="inf-login__blob inf-login__blob--c" />
          <div className="inf-login__brandinner">
            <p className="inf-login__eyebrow">For creators</p>
            <h1 className="inf-login__headline">
              Your campaigns, deliverables and <em>payments</em> in one place.
            </h1>
            <p className="inf-login__sub">
              Sign in with the account you create on. We use it to pull in your profile and post
              performance, so brands see the numbers that win you the next campaign.
            </p>
            <ul className="inf-login__points">
              {POINTS.map((point) => (
                <li className="inf-login__point" key={point}>
                  <span className="inf-login__tick" aria-hidden="true">
                    ✓
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <main className="inf-login__panel">
          <div className="inf-login__card">
            <img src={InfluencerLogo} alt="Influencer.in" className="inf-login__logo" />
            <h2 className="inf-login__title">Creator sign in</h2>
            <p className="inf-login__lede">
              Continue with the platform you create on — we&apos;ll set up your account
              automatically the first time.
            </p>

            <button
              type="button"
              className="inf-btn inf-btn--ig"
              onClick={startInstagram}
              disabled={busy || (!useDirectInstagram && !instagramReady)}
            >
              {pending === 'instagram' ? (
                <span className="inf-login__spinner" aria-hidden="true" />
              ) : (
                <InstagramMark />
              )}
              {pending === 'instagram' ? 'Signing you in…' : 'Continue with Instagram'}
            </button>

            <button
              type="button"
              className="inf-btn inf-btn--yt"
              onClick={() => startYoutube()}
              disabled={busy}
            >
              {pending === 'youtube' ? (
                <span className="inf-login__spinner" aria-hidden="true" />
              ) : (
                <YoutubeMark />
              )}
              {pending === 'youtube' ? 'Signing you in…' : 'Continue with YouTube'}
            </button>

            <p className="inf-login__hint">
              {useDirectInstagram
                ? 'You’ll sign in on Instagram and approve read-only access to your insights. No Facebook Page needed.'
                : 'Instagram creator accounts sign in through Facebook — that’s how Instagram connects business and creator profiles, so you’ll see a Facebook screen next.'}
            </p>

            {error ? (
              <p className="inf-login__error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="inf-login__divider">
              <span>ARE YOU A BRAND?</span>
            </div>
            <p className="inf-login__alt">
              <button type="button" className="inf-login__link" onClick={() => history.push('/login')}>
                Sign in to the brand dashboard
              </button>
            </p>

            <p className="inf-login__legal">
              By continuing you agree to our{' '}
              <a href="https://www.influencer.in/terms-and-conditions/" target="_blank" rel="noopener noreferrer">
                Terms
              </a>{' '}
              and{' '}
              <a href="https://www.influencer.in/privacy-policy/" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
              <br />
              Use of YouTube data is governed by the{' '}
              <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
                YouTube Terms of Service
              </a>{' '}
              and the{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google Privacy Policy
              </a>
              . You can revoke access anytime via{' '}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
                Google security settings
              </a>
              .
            </p>
          </div>
        </main>
      </div>
    </>
  );
}

export default withRouter(InfluencerLogin);
